import intl from "react-intl-universal";
import { TbCalendar, TbCurrencyReal, TbSwitchHorizontal } from "react-icons/tb";
import { ValutField, ValutInput } from "../../../../Components/Atoms/ValutInput";
import { ModernFilterBar } from "../../../../Components/Molecules/Filters/ModernFilterBar";
import {
  AmountFilterPanel,
  CheckboxListFilterPanel,
  DateFilterPanel,
  MoreFiltersAmountSection,
  MoreFiltersCheckboxSection,
  MoreFiltersDateSection,
} from "../../../../Components/Molecules/Filters/ModernFilterPanels";
import {
  EMPTY_PIX_CHARGES_FILTERS,
  buildPixChargesFilterTags,
  exportPixChargesCsv,
  getPixChargeStatuses,
  getPixChargeTypes,
  hasActivePixChargesFilters,
  isPixChargesChipFilterActive,
  removePixChargesFilterTag,
  togglePixChargeStatusFilter,
  togglePixChargeTypeFilter,
} from "./pixChargesFilters";

const FilterPill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`baas-filter-pill ${active ? "baas-filter-pill--active" : ""}`}
  >
    {children}
  </button>
);

const FilterPillRow = ({ label, children }) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
    <span className="shrink-0 pt-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 sm:w-16">
      {label}
    </span>
    <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">{children}</div>
  </div>
);

const PixChargesPillFilters = ({ filters, onFiltersChange }) => {
  const statuses = getPixChargeStatuses();
  const types = getPixChargeTypes();

  return (
    <div className="app-card mt-3 space-y-3 p-4">
      <FilterPillRow label={intl.get("pix.charges.filterStatus")}>
        {statuses.map((status) => (
          <FilterPill
            key={status.id}
            active={filters.statuses?.includes(status.id)}
            onClick={() => onFiltersChange(togglePixChargeStatusFilter(filters, status.id))}
          >
            {status.label}
          </FilterPill>
        ))}
      </FilterPillRow>

      <FilterPillRow label={intl.get("pix.charges.filterType")}>
        {types.map((type) => (
          <FilterPill
            key={type.id}
            active={filters.types?.includes(type.id)}
            onClick={() => onFiltersChange(togglePixChargeTypeFilter(filters, type.id))}
          >
            {type.label}
          </FilterPill>
        ))}
      </FilterPillRow>
    </div>
  );
};

const PixChargesMoreFiltersForm = ({ draft, onChange }) => (
  <div className="extract-more-filters-form">
    <MoreFiltersDateSection draft={draft} onChange={onChange} />
    <MoreFiltersAmountSection draft={draft} onChange={onChange} idPrefix="pix-charges-more" />
    <MoreFiltersCheckboxSection
      label={intl.get("pix.charges.filterStatus")}
      options={getPixChargeStatuses()}
      selectedValues={draft.statuses ?? []}
      onChange={(statuses) => onChange({ ...draft, statuses })}
      idPrefix="pix-charges-more-status"
    />
    <MoreFiltersCheckboxSection
      label={intl.get("pix.charges.filterType")}
      options={getPixChargeTypes()}
      selectedValues={draft.types ?? []}
      onChange={(types) => onChange({ ...draft, types })}
      idPrefix="pix-charges-more-type"
    />
    <section className="extract-more-filters-form__section">
      <ValutField label={intl.get("pix.charges.txid")} htmlFor="pix-charges-more-txid">
        <ValutInput
          id="pix-charges-more-txid"
          value={draft.txid}
          onChange={(event) => onChange({ ...draft, txid: event.target.value })}
        />
      </ValutField>
    </section>
    <section className="extract-more-filters-form__section">
      <ValutField label={intl.get("pix.charges.chargeId")} htmlFor="pix-charges-more-charge-id">
        <ValutInput
          id="pix-charges-more-charge-id"
          value={draft.chargeId}
          onChange={(event) => onChange({ ...draft, chargeId: event.target.value })}
        />
      </ValutField>
    </section>
  </div>
);

export const PixChargesFilterBar = ({ filters, onFiltersChange, filteredItems }) => {
  const filterButtons = [
    {
      id: "date",
      icon: TbCalendar,
      label: intl.get("extract.filterDate"),
      active: isPixChargesChipFilterActive(filters, "date"),
    },
    {
      id: "amount",
      icon: TbCurrencyReal,
      label: intl.get("extract.filterAmount"),
      active: isPixChargesChipFilterActive(filters, "amount"),
    },
    {
      id: "status",
      icon: TbSwitchHorizontal,
      label: intl.get("pix.charges.filterStatus"),
      active: isPixChargesChipFilterActive(filters, "status"),
    },
    {
      id: "type",
      icon: TbSwitchHorizontal,
      label: intl.get("pix.charges.filterType"),
      active: isPixChargesChipFilterActive(filters, "type"),
    },
  ];

  const renderPanelContent = (panelId, draft, onChange) => {
    switch (panelId) {
      case "date":
        return <DateFilterPanel draft={draft} onChange={onChange} />;
      case "amount":
        return <AmountFilterPanel draft={draft} onChange={onChange} idPrefix="pix-charges-filter" />;
      case "status":
        return (
          <CheckboxListFilterPanel
            options={getPixChargeStatuses()}
            selectedValues={draft.statuses ?? []}
            onChange={(statuses) => onChange({ ...draft, statuses })}
            idPrefix="pix-charges-status"
          />
        );
      case "type":
        return (
          <CheckboxListFilterPanel
            options={getPixChargeTypes()}
            selectedValues={draft.types ?? []}
            onChange={(types) => onChange({ ...draft, types })}
            idPrefix="pix-charges-type"
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ModernFilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        emptyFilters={EMPTY_PIX_CHARGES_FILTERS}
        searchPlaceholder={intl.get("pix.charges.searchPlaceholder")}
        filterButtons={filterButtons}
        moreFiltersActive={isPixChargesChipFilterActive(filters, "more")}
        buildFilterTags={buildPixChargesFilterTags}
        hasActiveFilters={hasActivePixChargesFilters}
        removeFilterTag={removePixChargesFilterTag}
        onExport={exportPixChargesCsv}
        filteredItems={filteredItems}
        renderPanelContent={renderPanelContent}
        renderMoreFilters={(draft, onChange) => (
          <PixChargesMoreFiltersForm draft={draft} onChange={onChange} />
        )}
        panelTitles={{
          date: intl.get("extract.filterDate"),
          amount: intl.get("extract.filterAmount"),
          status: intl.get("pix.charges.filterStatus"),
          type: intl.get("pix.charges.filterType"),
        }}
        exportEmptyMessageKey="pix.charges.noRecordsFiltered"
      />
      <PixChargesPillFilters filters={filters} onFiltersChange={onFiltersChange} />
    </>
  );
};
